import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useMatterStore } from './matterStore';

/**
 * Bulk row selection + session-only tagging for the references tables.
 *
 * Frontend-only POC: there is no backend support yet, so tag data lives in
 * sessionStorage (per tab, gone on refresh / new browser) and selection state
 * lives purely in memory. Tags are keyed by matter + reference content_hash,
 * so the same record shows its tags across different chats within the
 * session. When backend persistence lands, this store is the seam — the
 * components only talk to its API.
 */

/** A tag definition. `colorIndex` points into TAG_PALETTE. */
export interface ReferenceTag {
    name: string;
    colorIndex: number;
}

/** Resolved palette entry for rendering a tag pill. */
export interface TagColor {
    bg: string;
    text: string;
}

// Pastel palette matching the Figma label menu (green / yellow / purple /
// blue / red). New tags cycle through it in creation order.
export const TAG_PALETTE: TagColor[] = [
    { bg: '#d5f2dc', text: '#1d6334' },
    { bg: '#f6e3a2', text: '#7a5b0f' },
    { bg: '#e6dbf7', text: '#5b3a8f' },
    { bg: '#cfe3fb', text: '#1d4f8f' },
    { bg: '#fbd8db', text: '#8f1d2c' },
];

interface PersistedTagData {
    tags: ReferenceTag[];
    rowTags: Record<string, string[]>;
}

const storageKey = (matterId: string) => `pepper_reference_tags_${matterId}`;

export const useReferenceSelectionStore = defineStore('referenceSelection', () => {
    const matterStore = useMatterStore();

    // --- Selection (in-memory only) -------------------------------------

    // content_hash values of the checked rows in the active references view.
    const selectedHashes = ref<Set<string>>(new Set());

    const selectionCount = computed(() => selectedHashes.value.size);
    const hasSelection = computed(() => selectedHashes.value.size > 0);

    const isSelected = (hash: string | undefined) =>
        !!hash && selectedHashes.value.has(hash);

    function toggleRow(hash: string | undefined): void {
        if (!hash) return;
        const next = new Set(selectedHashes.value);
        if (next.has(hash)) {
            next.delete(hash);
        } else {
            next.add(hash);
        }
        selectedHashes.value = next;
    }

    /** Header checkbox: select all the given rows (the visible table). */
    function selectAll(hashes: Array<string | undefined>): void {
        const next = new Set(selectedHashes.value);
        for (const h of hashes) {
            if (h) next.add(h);
        }
        selectedHashes.value = next;
    }

    /** Header checkbox: unselect all the given rows. */
    function deselectAll(hashes: Array<string | undefined>): void {
        const next = new Set(selectedHashes.value);
        for (const h of hashes) {
            if (h) next.delete(h);
        }
        selectedHashes.value = next;
    }

    function clearSelection(): void {
        if (selectedHashes.value.size > 0) {
            selectedHashes.value = new Set();
        }
    }

    // --- Tags (sessionStorage, per matter) -------------------------------

    const tags = ref<ReferenceTag[]>([]);
    // content_hash -> tag names applied to that reference.
    const rowTags = ref<Record<string, string[]>>({});
    const loadedMatterId = ref<string | null>(null);

    function loadForMatter(matterId: string | null): void {
        loadedMatterId.value = matterId;
        tags.value = [];
        rowTags.value = {};
        if (!matterId) return;
        try {
            const raw = sessionStorage.getItem(storageKey(matterId));
            if (!raw) return;
            const parsed = JSON.parse(raw) as PersistedTagData;
            tags.value = Array.isArray(parsed.tags) ? parsed.tags : [];
            rowTags.value =
                parsed.rowTags && typeof parsed.rowTags === 'object' ? parsed.rowTags : {};
        } catch {
            // Corrupt / unavailable sessionStorage — start empty; tags are a
            // best-effort session convenience, not a load-bearing path.
        }
    }

    function persist(): void {
        const matterId = loadedMatterId.value;
        if (!matterId) return;
        try {
            sessionStorage.setItem(
                storageKey(matterId),
                JSON.stringify({ tags: tags.value, rowTags: rowTags.value }),
            );
        } catch {
            // See loadForMatter — best-effort.
        }
    }

    // Tag data follows the open matter; selection never survives a matter
    // switch.
    watch(
        () => matterStore.currentMatter?.RecordId ?? null,
        (matterId) => {
            clearSelection();
            loadForMatter(matterId);
        },
        { immediate: true },
    );

    const colorForTag = (tag: ReferenceTag): TagColor =>
        TAG_PALETTE[((tag.colorIndex % TAG_PALETTE.length) + TAG_PALETTE.length) % TAG_PALETTE.length];

    const findTag = (name: string) => tags.value.find((t) => t.name === name);

    /** Tags applied to a single row, alphabetized. */
    function tagsForRow(hash: string | undefined): ReferenceTag[] {
        if (!hash) return [];
        const names = rowTags.value[hash];
        if (!names || names.length === 0) return [];
        return tags.value
            .filter((t) => names.includes(t.name))
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    }

    /**
     * Whether the given rows carry the tag: 'all' (every row has it),
     * 'some', or 'none'. Drives the checkmark in the tag menu. Callers pass
     * the bulk selection or a single raw-view reference alike.
     */
    function tagStateForRows(hashes: string[], name: string): 'all' | 'some' | 'none' {
        if (hashes.length === 0) return 'none';
        let count = 0;
        for (const hash of hashes) {
            if (rowTags.value[hash]?.includes(name)) count++;
        }
        if (count === 0) return 'none';
        return count === hashes.length ? 'all' : 'some';
    }

    /** Create a tag (next palette color). Returns the existing one on a name hit. */
    function createTag(name: string): ReferenceTag | null {
        const trimmed = name.trim();
        if (!trimmed) return null;
        const existing = findTag(trimmed);
        if (existing) return existing;
        const tag: ReferenceTag = { name: trimmed, colorIndex: tags.value.length };
        tags.value = [...tags.value, tag];
        persist();
        return tag;
    }

    /**
     * Toggle a tag across the given rows: if every row already has it,
     * remove it from all of them; otherwise add it to all of them.
     */
    function toggleTagOnRows(hashes: string[], name: string): void {
        if (!findTag(name) || hashes.length === 0) return;
        const removing = tagStateForRows(hashes, name) === 'all';
        const next = { ...rowTags.value };
        for (const hash of hashes) {
            const current = next[hash] || [];
            if (removing) {
                const filtered = current.filter((n) => n !== name);
                if (filtered.length > 0) {
                    next[hash] = filtered;
                } else {
                    delete next[hash];
                }
            } else if (!current.includes(name)) {
                next[hash] = [...current, name];
            }
        }
        rowTags.value = next;
        persist();
    }

    /** Create (if needed) and apply a tag to the given rows. */
    function createAndApplyTag(name: string, hashes: string[]): void {
        const tag = createTag(name);
        // Always additive: the tag was just created (or matched by name), so
        // "apply" must never flip into toggleTagOnRows' remove branch.
        if (!tag || hashes.length === 0) return;
        const next = { ...rowTags.value };
        for (const hash of hashes) {
            const current = next[hash] || [];
            if (!current.includes(tag.name)) {
                next[hash] = [...current, tag.name];
            }
        }
        rowTags.value = next;
        persist();
    }

    return {
        selectedHashes,
        selectionCount,
        hasSelection,
        isSelected,
        toggleRow,
        selectAll,
        deselectAll,
        clearSelection,
        tags,
        rowTags,
        colorForTag,
        tagsForRow,
        tagStateForRows,
        createTag,
        toggleTagOnRows,
        createAndApplyTag,
    };
});
